import { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'
import { experimentsApi } from '@/lib/api'
import { useExperimentListRealtime } from '@/lib/realtime/useExperimentListRealtime'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'

interface ExperimentListStatus {
  status?: string
  reportStatus?: string
  deckStatus?: string
  deckJsonKey?: string | null
}

export interface ExperimentStats {
  completed: number
  running: number
  totalRespondents: number
}

export function shouldRefreshExperiments(
  experiments?: Array<ExperimentListStatus>,
) {
  return (experiments || []).some((experiment) => {
    if (experiment.status === 'pending' || experiment.status === 'running') {
      return true
    }
    if (experiment.status !== 'completed') {
      return false
    }

    const reportReady = experiment.reportStatus === 'ready'
    const deckReady =
      experiment.deckStatus === 'completed' ||
      experiment.deckStatus === 'skipped' ||
      !!(experiment.deckJsonKey && String(experiment.deckJsonKey).trim())
    return !reportReady || !deckReady
  })
}

export function useExperimentsPaginated(initialLimit = 9) {
  const [page, setPage] = useState(1)
  const [allExperiments, setAllExperiments] = useState<any[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [stats, setStats] = useState<ExperimentStats | null>(null)
  const [total, setTotal] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const { data, isLoading, error, mutate } = useSWR(
    `experiments-page-${page}-${initialLimit}`,
    () => experimentsApi.listPaginated(page, initialLimit),
    {
      revalidateOnFocus: true,
      refreshInterval: legacyRefreshInterval('STUDIES', 5000),
    },
  )

  const invalidate = useCallback(() => {
    void mutate()
  }, [mutate])

  useExperimentListRealtime({
    active: shouldRefreshExperiments(data?.experiments ?? allExperiments),
    onInvalidate: invalidate,
  })

  useEffect(() => {
    if (!data?.experiments) return

    setAllExperiments((prev) => {
      if (page === 1) return data.experiments

      const existingIds = new Set(prev.map((e: any) => e.id))
      const newExperiments = data.experiments.filter(
        (e: any) => !existingIds.has(e.id),
      )
      return [...prev, ...newExperiments]
    })
    setHasMore(data.hasMore)
    setTotal(data.total)
    if (data.stats) setStats(data.stats)
    setIsLoadingMore(false)
  }, [data, page])

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true)
      setPage((p) => p + 1)
    }
  }, [hasMore, isLoadingMore])

  const reset = useCallback(() => {
    setPage(1)
    setAllExperiments([])
    setHasMore(true)
    mutate()
  }, [mutate])

  const updateExperiment = useCallback((id: string, updates: Partial<any>) => {
    setAllExperiments((prev) =>
      prev.map((exp) => (exp.id === id ? { ...exp, ...updates } : exp)),
    )
  }, [])

  const removeExperiment = useCallback((id: string) => {
    setAllExperiments((prev) => prev.filter((exp) => exp.id !== id))
    setTotal((prev) => Math.max(0, prev - 1))
  }, [])

  const stillProcessing =
    !isLoading &&
    data?.experiments &&
    allExperiments.length === 0 &&
    data.experiments.length > 0

  return {
    experiments: allExperiments,
    stats,
    total,
    isLoading: (isLoading && page === 1) || stillProcessing,
    isLoadingMore: isLoadingMore || (isLoading && page > 1),
    hasMore,
    loadMore,
    reset,
    updateExperiment,
    removeExperiment,
    error,
    mutate,
  }
}
